import LocalizedTextField from '@components/Customization/LocalizedTextField';
import RuleEditor, { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import ToggleRow from '@components/Customization/ToggleRow';
import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, Drawer, IconButton, Stack, TextField, Typography } from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
} from '@pages/CustomPoiTypes.styles';
import { hasText, type PoiFieldGroup } from '@store/api/poiTypeEndpoints';
import { useEffect, useState } from 'react';
import { TimesIcon } from 'react-line-awesome';

/** A group's rule sets visibility and nothing else — see `IPoiFieldGroupRule`. */
const GROUP_TARGETS = ['visible'] as const;

const emptyGroup = (): PoiFieldGroup => ({ name: {}, description: '', visible: true, fields: [] });

interface Props {
  open: boolean;
  /** The group being renamed, or null when adding one. */
  group: PoiFieldGroup | null;
  /** Fields a rule may point at: this type's own and every ancestor's, already resolved. */
  candidates: RuleCandidate[];
  typeName: string;
  saving: boolean;
  onClose: () => void;
  onSave: (group: PoiFieldGroup) => void;
}

const GroupDrawer: React.FC<Props> = ({ open, group, candidates, typeName, saving, onClose, onSave }) => {
  const { t } = useLocalization();

  const [draft, setDraft] = useState<PoiFieldGroup>(emptyGroup());

  useEffect(() => {
    if (!open) return;
    setDraft(group ? { ...group } : emptyGroup());
  }, [open, group]);

  const canSave = hasText(draft.name) && !saving;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Matches the field drawer: a condition row does not fit in 440.
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 560 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {group ? t('Customization_EditGroup') : t('Customization_NewGroup')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Customization_GroupSubtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Stack spacing={2} sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          <LocalizedTextField
            required
            autoFocus
            label={t('Customization_GroupName')}
            placeholder={t('Customization_GroupNamePlaceholder')}
            value={draft.name}
            onChange={(name) => setDraft((current) => ({ ...current, name }))}
            helperText={t('Customization_GroupNameHelp')}
          />
          <TextField
            fullWidth
            size="small"
            label={t('Common_Description')}
            placeholder={t('Customization_GroupDescriptionPlaceholder')}
            value={draft.description ?? ''}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
          />

          <Divider sx={{ pt: 1 }} />

          <ToggleRow
            label={t('Customization_GroupVisible')}
            description={t('Customization_GroupVisibleHelp')}
            checked={draft.visible !== false}
            onChange={(visible) => setDraft((current) => ({ ...current, visible }))}
          />

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {t('Customization_Rules')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {t('Customization_GroupRulesHelpStatus')}
            </Typography>
            <RuleEditor
              rules={draft.rules ?? []}
              // A group's `then` only ever carries `visible`, and the editor is told to
              // offer nothing else — so the narrowing here is reading back what it wrote.
              onChange={(rules) => setDraft((current) => ({
                ...current,
                rules: rules.length
                  ? rules.map((rule) => ({ ...rule, then: { visible: rule.then.visible ?? true } }))
                  : undefined,
              }))}
              candidates={candidates}
              targets={GROUP_TARGETS}
              baseVisible={draft.visible !== false}
              onBaseVisibleChange={(visible) => setDraft((current) => ({ ...current, visible }))}
              disabled={saving}
            />
          </Box>
        </Stack>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={drawerFooterStyles}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {t('Customization_GroupSavesTo', [typeName])}
          </Typography>
          <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={!canSave}
            onClick={() => onSave(draft)}
            sx={buttonStyles}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default GroupDrawer;
